"use client";

import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { ChevronDown } from "lucide-react";
import type { Transition } from "motion/react";
import { motion } from "motion/react";
import React from "react";
import MotionHighlight from "@/components/effects/MotionHighlight";
import Flex from "@/components/layout/Flex";
import Typography from "@/components/shared/Typography";
import Visibility from "@/components/shared/Visibility";
import { breakpointClasses, cn, splitBreakpointProps } from "@/lib/utils";
import { useAccordionItem } from "../provider";
import type { AccordionTriggerProps } from "../types";

const DEFAULT_TRANSITION: Transition = {
  type: "spring",
  stiffness: 150,
  damping: 22,
};

function AccordionTrigger({
  transition = DEFAULT_TRANSITION,
  chevron = true,
  ref,
  className,
  children,
  ...props
}: AccordionTriggerProps) {
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);
  const { isOpen, setIsOpen } = useAccordionItem();

  React.useImperativeHandle(ref, () => triggerRef.current as HTMLButtonElement);

  const { bp, native } = splitBreakpointProps(props);
  const classes = breakpointClasses(bp);

  React.useEffect(() => {
    const node = triggerRef.current;

    if (!node) {
      return;
    }

    const observer = new MutationObserver(mutationsList => {
      mutationsList.forEach(mutation => {
        if (mutation.attributeName === "data-state") {
          const currentState = node.getAttribute("data-state");

          setIsOpen(currentState === "open");
        }
      });
    });

    observer.observe(node, {
      attributes: true,
      attributeFilter: ["data-state"],
    });

    const initialState = node.getAttribute("data-state");
    setIsOpen(initialState === "open");

    return () => {
      observer.disconnect();
    };
  });

  return (
    <MotionHighlight.Item>
      <AccordionPrimitive.Header data-slot="accordion-header" className="flex">
        <Flex asChild justify="between" items="center" className="flex-1">
          <AccordionPrimitive.Trigger
            ref={triggerRef}
            data-slot="accordion-trigger"
            className={cn(
              "py-4 font-medium hover:underline hover:bg-accent bg-card p-4 border-solid border-border border-b-2 transition-all duration-300",
              className,
              classes,
            )}
            {...native}
          >
            <Typography.H3 className="text-start">{children}</Typography.H3>

            <Visibility shouldShow={chevron}>
              <motion.div
                data-slot="accordion-trigger-chevron"
                animate={{ rotate: isOpen ? 180 : 0 }}
                transition={transition}
              >
                <ChevronDown className="size-5 shrink-0" />
              </motion.div>
            </Visibility>
          </AccordionPrimitive.Trigger>
        </Flex>
      </AccordionPrimitive.Header>
    </MotionHighlight.Item>
  );
}

AccordionTrigger.displayName = "Accordion.Trigger";

export default AccordionTrigger;
